// PR-C: the pre-submit checklist. One block, above Generate, where every sensitive value is confirmed at once --
// decisions (inputs that must be chosen) and review items (what the file will say, with a tick). Review values come
// from a dry-run build of the real file on the server, never from a second calculation here.
//
// Deliberately NOT here: any "header Packages != sum of line CTNS" warning -- it is normally unequal (see
// src/lib/ciNormalise.ts) and flagging it would send staff off to "fix" correct data.
import type { V2Decisions, PrecheckResult, DanMode, DeclType, LoadType, PvaMode } from '../../hooks/useECustoms'
import { checklistStatus, isRoadMode } from '../../lib/precheckStatus'

interface Props {
  transportMode: string | null
  importerName: string | null
  importerPvaFlag: boolean | null
  danDefault: boolean | null
  danDefaultSource: string | null
  decisions: V2Decisions
  danOverridden: boolean
  repOverridden: boolean
  loadOverridden: boolean
  precheck: PrecheckResult | null
  loading: boolean
  ticks: Record<string, string>
  onTick: (id: string, value: string, checked: boolean) => void
  set: {
    pva: (v: PvaMode) => void; dan: (v: DanMode) => void; declType: (v: DeclType) => void
    rep: (v: '2' | '3') => void; load: (v: LoadType) => void; arrival: (v: string) => void
    mrn: (v: string) => void; vatOverride: (v: number | null) => void
  }
}

const ARRIVAL_LABEL: Record<string, string> = { sea: 'vessel name', air: 'flight number', truck: 'tractor + trailer registration', road: 'tractor + trailer registration' }

function Radios<T extends string>({ name, value, options, onChange }: { name: string; value: T | null; options: [T, string][]; onChange: (v: T) => void }) {
  return (
    <div className="flex flex-wrap gap-x-6 gap-y-1">
      {options.map(([k, label]) => (
        <label key={k} className="flex items-center gap-2 text-gray-800 dark:text-gray-200">
          <input type="radio" name={name} checked={value === k} onChange={() => onChange(k)} /> {label}
        </label>
      ))}
    </div>
  )
}

export default function PreSubmitChecklist(p: Props) {
  const d = p.decisions
  const st = checklistStatus(d, p.precheck, p.ticks, p.transportMode)
  const missingIds = new Set(st.missing.map(m => m.id))
  const box = (id: string) => `rounded-lg border p-3 text-sm ${missingIds.has(id) ? 'border-amber-400 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20' : 'border-gray-200 dark:border-gray-700'}`
  const title = 'mb-2 font-semibold text-gray-900 dark:text-white'
  const hint = 'mt-2 text-xs text-gray-500 dark:text-gray-400'
  const req = <span className="text-red-600"> *</span>
  const va = p.precheck?.vatAdjustment
  const modeKey = (p.transportMode || '').toLowerCase()

  return (
    <section className="mb-6 rounded-xl border border-gray-300 p-4 dark:border-gray-600" aria-label="Pre-submit checklist">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white">Check before generating</h3>
        <span className={`rounded px-2 py-0.5 text-xs font-bold ${st.complete ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200' : 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200'}`}>
          {st.complete ? 'Ready' : p.loading || !p.precheck ? 'Checking…' : `${st.remaining} to go`}
        </span>
      </div>

      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Decisions -- choose each one</p>
      <div className="grid gap-3 md:grid-cols-2">
        <div className={box('decl_type')}>
          <p className={title}>Declaration Type{req}</p>
          <Radios name="decl-type" value={d.declType} onChange={p.set.declType} options={[['IMD', 'IMD -- goods not yet arrived'], ['IMA', 'IMA -- goods arrived']]} />
          <p className={hint}>Staff decision. Check the job's ETA against today before choosing.</p>
        </div>
        <div className={box('rep')}>
          <p className={title}>Declarant Representation{req}</p>
          <Radios name="rep" value={d.declarantRep} onChange={p.set.rep} options={[['2', '2 -- direct (UK importer)'], ['3', '3 -- indirect']]} />
          <p className={hint}>{p.repOverridden ? 'Overridden for this declaration.' : `Pre-filled from ${p.importerName ?? 'the importer'}'s country (UK = 2, otherwise 3).`}</p>
        </div>
        <div className={box('pva')}>
          <p className={title}>VAT treatment{req}</p>
          <Radios name="pva-mode" value={d.pvaMode} onChange={p.set.pva} options={[['PVA', 'PVA (postponed VAT accounting)'], ['NON_PVA', 'Non-PVA']]} />
          <p className={hint}>
            {p.importerName ?? 'The importer'}&apos;s customer record says: {p.importerPvaFlag === true ? 'PVA' : p.importerPvaFlag === false ? 'not PVA' : 'not set'}.
            {' '}For reference only -- that flag was set in bulk and is not pre-selected. Applies to this declaration only; not saved to the customer.
          </p>
        </div>
        <div className={box('dan')}>
          <p className={title}>Duty &amp; VAT payment{req}</p>
          <Radios name="dan-mode" value={d.danMode} onChange={p.set.dan} options={[['DAN', 'Pay via CY UK DAN (deferment account)'], ['NO_DAN', 'No DAN']]} />
          <p className={hint}>
            {p.danOverridden
              ? `Overridden for this declaration${p.danDefaultSource ? ` (${p.danDefaultSource}'s default is ${p.danDefault ? 'use CY UK DAN' : 'no DAN'})` : ''}.`
              : p.danDefault != null ? `Pre-filled from ${p.danDefaultSource ?? 'the billing customer'}'s customer record (default: ${p.danDefault ? 'use CY UK DAN' : 'no DAN'}).` : 'No customer default found.'}
            {' '}Using our DAN means CY UK advances the duty/VAT and re-bills -- make sure the deferment fee is on the job. Not saved to the customer.
          </p>
        </div>
        <div className={box('load')}>
          <p className={title}>Load type{req}</p>
          <Radios name="load" value={d.loadType} onChange={p.set.load} options={[['FCL', 'FCL 整柜'], ['LCL', 'LCL 散货'], ['AIR', 'Air 空运']]} />
          <p className={hint}>{p.loadOverridden ? 'Chosen by you.' : modeKey === 'air' ? 'Pre-selected: air job.' : 'Drives the VAT Adjustment below.'}</p>
          {va?.applicable && (
            <div className="mt-2 rounded border border-gray-200 p-2 dark:border-gray-700">
              <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">VAT Adjustment</p>
              <p className="text-xs text-gray-600 dark:text-gray-400">{va.calculated.formula}{va.calculated.minApplied ? '' : ''}</p>
              {d.loadType === 'LCL' && <p className="text-xs text-amber-700 dark:text-amber-300">吨数取值规则待确认（现在用精确吨数，不进位）</p>}
              <div className="mt-1 flex items-center gap-2">
                <input
                  type="number" step="0.01" className="w-28 rounded border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                  value={d.vatAdjOverride ?? va.calculated.amount}
                  onChange={e => p.set.vatOverride(e.target.value === '' ? null : parseFloat(e.target.value))}
                /> <span className="text-xs">GBP</span>
                {d.vatAdjOverride != null && (
                  <>
                    <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-900 dark:bg-amber-900/40 dark:text-amber-200">edited by hand</span>
                    <button type="button" className="text-xs text-blue-600 underline" onClick={() => p.set.vatOverride(null)}>use calculated {va.calculated.amount.toFixed(2)}</button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
        <div className={box('arrival')}>
          <p className={title}>Arrival Transport ID{req} <span className="ml-1 text-xs font-normal text-gray-500">{ARRIVAL_LABEL[modeKey] ?? 'vessel name (sea) · flight number (air) · tractor + trailer registration (truck)'} (AA.22)</span></p>
          <input
            value={d.arrivalId} maxLength={27} placeholder="vessel / flight / vehicle"
            onChange={e => p.set.arrival(e.target.value)}
            onBlur={e => p.set.arrival(e.target.value.trim().toUpperCase().replace(/\s+/g, ' '))}
            className="w-64 rounded-lg border border-gray-300 bg-white px-3 py-1.5 font-mono text-sm uppercase text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
          />
          {(p.precheck?.missing || []).filter(m => m.startsWith('Arrival Transport ID')).map(m => <p key={m} className="mt-1 text-xs font-medium text-amber-800 dark:text-amber-300">{m}</p>)}
        </div>
        {isRoadMode(p.transportMode) && (
          <div className={box('mrn')}>
            <p className={title}>Transit MRN (T1){req}</p>
            <input value={d.transitMrn} onChange={e => p.set.mrn(e.target.value.toUpperCase())} placeholder="26GB…" className="w-64 rounded-lg border border-gray-300 bg-white px-3 py-1.5 font-mono text-sm uppercase text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white" />
            <p className={hint}>Road jobs only. Typed here for now.</p>
          </div>
        )}
      </div>

      <p className="mb-2 mt-4 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Review -- values as they will appear in the file; tick each one</p>
      {st.blocker && <p className="mb-2 rounded border border-red-300 bg-red-50 p-2 text-sm font-medium text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">Cannot build the file yet: {st.blocker}</p>}
      <ul className="grid gap-2 md:grid-cols-2">
        {(p.precheck?.review || []).map(r => {
          const ticked = p.ticks[r.id] === r.value && !!r.value
          return (
            <li key={r.id} className={`flex items-start gap-2 rounded-lg border p-2 text-sm ${!ticked ? 'border-amber-300 dark:border-amber-700' : 'border-gray-200 dark:border-gray-700'}`}>
              <input type="checkbox" className="mt-1" checked={ticked} disabled={!r.value} onChange={e => p.onTick(r.id, r.value, e.target.checked)} />
              <span>
                <span className="block text-xs text-gray-500 dark:text-gray-400">{r.label}</span>
                <span className={`font-mono ${r.value ? 'text-gray-900 dark:text-white' : 'font-semibold text-red-700 dark:text-red-300'}`}>{r.value || 'EMPTY'}</span>
              </span>
            </li>
          )
        })}
        {!p.precheck && <li className="text-sm text-gray-500">Loading the values from the file that would be generated…</li>}
      </ul>

      {(st.reds.length > 0 || st.yellows.length > 0) && (
        <div className="mt-4 space-y-1">
          {st.reds.map(c => <p key={c.id} className="rounded border border-red-300 bg-red-50 p-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">✕ {c.message}</p>)}
          {st.yellows.map(c => <p key={c.id} className="rounded border border-yellow-300 bg-yellow-50 p-2 text-sm text-yellow-900 dark:border-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-100">⚠ {c.message}</p>)}
        </div>
      )}

      {!st.complete && !p.loading && p.precheck && (
        <p className="mt-3 text-sm font-medium text-amber-800 dark:text-amber-300">
          Still to do ({st.remaining}): {[...st.missing.map(m => m.label), ...st.unticked.map(r => `tick ${r.label}`), ...st.reds.map(() => 'fix the red check(s)'), ...(st.blocker ? ['resolve the problem above'] : [])].join(' · ')}
        </p>
      )}
    </section>
  )
}
